import { useState, useCallback } from 'react';

interface UseExpandableResult {
  expandedId: string | null;
  toggle: (id: string) => void;
  expand: (id: string) => void;
  collapse: () => void;
  isExpanded: (id: string) => boolean;
}

export function useExpandable(initialExpanded: string | null = null): UseExpandableResult {
  const [expandedId, setExpandedId] = useState<string | null>(initialExpanded);

  const toggle = useCallback((id: string) => {
    setExpandedId(prev => prev === id ? null : id);
  }, []);

  const expand = useCallback((id: string) => {
    setExpandedId(id);
  }, []);

  const collapse = useCallback(() => {
    setExpandedId(null);
  }, []);

  const isExpanded = useCallback((id: string) => {
    return expandedId === id;
  }, [expandedId]);

  return { expandedId, toggle, expand, collapse, isExpanded };
}

interface UseMultiExpandableResult {
  expandedIds: string[];
  toggle: (id: string) => void;
  expand: (id: string) => void;
  collapse: (id: string) => void;
  isExpanded: (id: string) => boolean;
  expandAll: (ids: string[]) => void;
  collapseAll: () => void;
}

export function useMultiExpandable(initialExpanded: string[] = []): UseMultiExpandableResult {
  const [expandedIds, setExpandedIds] = useState<string[]>(initialExpanded);

  const toggle = useCallback((id: string) => {
    setExpandedIds(prev =>
      prev.includes(id)
        ? prev.filter(i => i !== id)
        : [...prev, id]
    );
  }, []);

  const expand = useCallback((id: string) => {
    setExpandedIds(prev =>
      prev.includes(id) ? prev : [...prev, id]
    );
  }, []);

  const collapse = useCallback((id: string) => {
    setExpandedIds(prev => prev.filter(i => i !== id));
  }, []);

  const isExpanded = useCallback((id: string) => {
    return expandedIds.includes(id);
  }, [expandedIds]);

  const expandAll = useCallback((ids: string[]) => {
    setExpandedIds(ids);
  }, []);

  const collapseAll = useCallback(() => {
    setExpandedIds([]);
  }, []);

  return { expandedIds, toggle, expand, collapse, isExpanded, expandAll, collapseAll };
}

